// W33 (2026-10-20): live HistorySource for the History tab.
//
//   useLiveHistorySource(customerId)
//     -> GET /v1/contacts/:id            (via useContactProfile, W25/W26)
//     -> GET /v1/contacts/:id/timeline?limit=50   (this file; notes + calls)
//
// The gate and header contract come from screens/_shared/useContactProfile.ts
// so there is exactly one definition of "real session". Preview (?preview=)
// and the dev mock session report status 'skipped'; the screen then, and
// only then, falls back to the approved Sarah example in model.ts.
//
// A failed or empty read never falls back to fixture data. The screen renders
// an honest empty per section, or the unavailable card with the error code.
//
// Timeline row shape (MEASURED 2026-10-20, services/api-server/sinks/
// contact-timeline-sink.mjs): { type: 'note' | 'call', id, at (ISO | null),
// body (HTML stripped), title, ownerId, durationMs?, disposition? }.
//
// Extension points:
//   projectOrderScopeEntries  W29: scope=orders rows from
//                             GET /api/customers/:id/history?scope=orders
//                             (defined and wired, NOT yet fed by any call)
//   projectProfileOrderProps  W34: order props on the /v1/contacts/:id profile
//                             (HubSpot shopify_orders_count, last_order_name,
//                             last_order_date, total_spent). FED since W35
//                             (2026-10-20): readProfileOrderProps() lifts them
//                             off the profile the hook already fetched.

import { useEffect, useMemo, useRef, useState } from 'react';

import type { MockCustomer } from '../../data/mockData.js';
import type { HubSpotSession } from '../../auth/hubspot-handshake.js';
import {
  buildSessionHeaders,
  useContactProfile,
  useContactReadGate,
  type ContactProfileRecord,
} from '../_shared/useContactProfile.js';
import type { TimelineItem } from '../_shared/useContactCollections.js';
import type { HistoryEvent, HistoryOwner, HistoryScope, HistorySource } from './model.js';

export const LIVE_TIMELINE_LIMIT = 50;

// ---------------------------------------------------------------------------
// Timeline rows -> HistoryEvent
// ---------------------------------------------------------------------------

const VANILLASOFT_CALL_PREFIX = /^VS Call\b/;

/**
 * The 2026-10-20 VanillaSoft import wrote one HubSpot NOTE per historical
 * call with a body starting "VS Call ..." (READ: contact-timeline-sink.mjs
 * header). Those rows are calls in substance, so they land under Calls /
 * Latest conversation rather than Notes.
 */
export function isVanillaSoftCallNote(item: Pick<TimelineItem, 'type' | 'body'>): boolean {
  return item.type === 'note' && VANILLASOFT_CALL_PREFIX.test((item.body ?? '').trim());
}

function parseAt(at: string | null | undefined): number | null {
  if (typeof at !== 'string' || at.length === 0) return null;
  const ms = Date.parse(at);
  return Number.isFinite(ms) ? ms : null;
}

function firstLine(body: string, max = 80): string {
  const line = body.split('\n').map(part => part.trim()).find(part => part.length > 0) ?? '';
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
}

function ownerLabel(ownerId: string | null | undefined): string {
  return ownerId ? `HubSpot owner ${ownerId}` : 'Owner not recorded';
}

function durationLabel(durationMs: number | null | undefined): string | null {
  if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs < 0) return null;
  return `${Math.round(durationMs / 1000)}s`;
}

export function projectTimelineItem(customerId: string, item: TimelineItem, index: number): HistoryEvent {
  const body = typeof item.body === 'string' ? item.body.trim() : '';
  const id = item.id ? `hs-${item.type}-${item.id}` : `hs-${item.type}-index-${index}`;
  const eventAt = parseAt(item.at);
  const base = { id, customerId, eventAt, body: body || 'No text recorded.' };
  const read = `Read from HubSpot engagement ${item.id ?? '(no id)'} through /v1/contacts/:id/timeline.`;
  if (item.type === 'call') {
    const duration = durationLabel(item.durationMs);
    return {
      ...base,
      kind: 'call',
      title: item.title?.trim() || 'Call logged in HubSpot',
      status: item.disposition?.trim() || 'Logged',
      actor: ownerLabel(item.ownerId),
      source: 'HubSpot call',
      detail: [read, duration ? `Duration ${duration}.` : null].filter(Boolean).join(' '),
    };
  }
  if (isVanillaSoftCallNote(item)) {
    return {
      ...base,
      kind: 'call',
      title: 'VanillaSoft call',
      status: 'Imported from VanillaSoft',
      actor: ownerLabel(item.ownerId),
      source: 'HubSpot note · VanillaSoft import',
      detail: `${read} Written by the 2026-10-20 VanillaSoft import; the body is the VanillaSoft call record.`,
    };
  }
  return {
    ...base,
    kind: 'note',
    title: firstLine(body) || 'Note',
    status: 'Note on file',
    actor: ownerLabel(item.ownerId),
    source: 'HubSpot note',
    detail: read,
  };
}

export function projectTimelineItems(customerId: string, items: readonly TimelineItem[]): HistoryEvent[] {
  return items
    .filter(item => item && (item.type === 'note' || item.type === 'call'))
    .map((item, index) => projectTimelineItem(customerId, item, index));
}

// ---------------------------------------------------------------------------
// EXTENSION POINT (W29): scope=orders entries from /api/customers/:id/history
// ---------------------------------------------------------------------------

/**
 * Minimal row the W29 route is expected to return per order. Every field is
 * optional so W29 can widen it without touching this screen; only `id` is
 * required to key the event. Nothing calls this with live data yet: the
 * route returns 503 CUSTOMER_HISTORY_NOT_CONFIGURED today (MEASURED
 * 2026-10-20).
 */
export interface OrderScopeEntry {
  id: string;
  orderName?: string | null;
  placedAt?: string | null;
  status?: string | null;
  total?: string | number | null;
  lineSummary?: string | null;
  source?: string | null;
}

export function projectOrderScopeEntries(customerId: string, entries: readonly OrderScopeEntry[]): HistoryEvent[] {
  return entries
    .filter(entry => entry && typeof entry.id === 'string' && entry.id.length > 0)
    .map(entry => {
      const name = entry.orderName?.trim() || entry.id;
      const total = entry.total === null || entry.total === undefined || entry.total === '' ? null : String(entry.total);
      return {
        id: `order-${entry.id}`,
        customerId,
        kind: 'order' as const,
        eventAt: parseAt(entry.placedAt),
        title: `Order ${name}`,
        body: [entry.lineSummary?.trim() || null, total ? `Total ${total}` : null].filter(Boolean).join(' · ') || 'Order details not returned.',
        status: entry.status?.trim() || 'Status not returned',
        actor: 'Shopify',
        source: entry.source?.trim() || 'Order history (scope=orders)',
        detail: 'Read from /api/customers/:id/history?scope=orders. Order status is the sync status, not a delivery promise.',
      };
    });
}

// ---------------------------------------------------------------------------
// EXTENSION POINT (W34): order props on the /v1/contacts/:id profile
// ---------------------------------------------------------------------------

/**
 * HubSpot contact properties on the /v1/contacts/:id profile since W34
 * (camelCase; MEASURED 2026-10-20 by W34 against the local api-server,
 * scratchpad/w34-local-probes/*-profile.json):
 *   Jodi 573130771181:  shopifyOrdersCount=1,  lastOrderName=#EQ635104, lastOrderDate=2026-10-02, totalSpent=185.25
 *   Karla 573074999013: shopifyOrdersCount=2,  lastOrderName=#EQ192790, lastOrderDate=2023-07-14, totalSpent=149
 *   Tere 572918887122:  shopifyOrdersCount=19, lastOrderName=#EQ635413, lastOrderDate=2026-10-04, totalSpent=3804.07
 * W35 feeds them through readProfileOrderProps() below.
 */
export interface ProfileOrderProps {
  shopifyOrdersCount?: number | string | null;
  lastOrderName?: string | null;
  lastOrderDate?: string | null;
  totalSpent?: number | string | null;
}

/**
 * W35: lift the W34 order props off the fetched profile. The W26
 * ContactProfileRecord type (screens/_shared, outside this wave's lane)
 * does not declare them, so they are read structurally; a profile from a
 * pre-W34 api-server simply yields null and the Latest order section
 * keeps its honest empty.
 */
export function readProfileOrderProps(profile: ContactProfileRecord | null | undefined): ProfileOrderProps | null {
  if (!profile) return null;
  const p = profile as ContactProfileRecord & Partial<ProfileOrderProps>;
  const out: ProfileOrderProps = {};
  if (typeof p.shopifyOrdersCount === 'number' || typeof p.shopifyOrdersCount === 'string') out.shopifyOrdersCount = p.shopifyOrdersCount;
  if (typeof p.lastOrderName === 'string') out.lastOrderName = p.lastOrderName;
  if (typeof p.lastOrderDate === 'string') out.lastOrderDate = p.lastOrderDate;
  if (typeof p.totalSpent === 'number' || typeof p.totalSpent === 'string') out.totalSpent = p.totalSpent;
  return Object.keys(out).length > 0 ? out : null;
}

/** '$185.25' / '$3,804.07'. HubSpot total_spent is the Shopify shop-currency amount (USD store). */
function formatSpent(value: number | string | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function projectProfileOrderProps(customerId: string, props: ProfileOrderProps | null | undefined): HistoryEvent | null {
  if (!props) return null;
  const name = props.lastOrderName?.trim() || null;
  const date = props.lastOrderDate?.trim() || null;
  if (!name && !date) return null;
  const count = props.shopifyOrdersCount === null || props.shopifyOrdersCount === undefined || props.shopifyOrdersCount === '' ? null : Number(props.shopifyOrdersCount);
  const spent = formatSpent(props.totalSpent);
  return {
    id: `order-last-${name ?? date}`,
    customerId,
    kind: 'order',
    eventAt: parseAt(date ? `${date}T12:00:00Z` : null),
    title: name ? `Last order ${name}` : 'Last order',
    body: [
      date ? `Placed ${date}` : null,
      count !== null && Number.isFinite(count) ? `${count} order${count === 1 ? '' : 's'} on file` : null,
      spent ? `Total spent ${spent}` : null,
    ].filter(Boolean).join(' · '),
    status: 'From HubSpot contact properties',
    actor: 'Shopify sync',
    source: 'HubSpot contact properties',
    detail: 'last_order_name / last_order_date / shopify_orders_count / total_spent as synced to the HubSpot contact. Line items are not on the contact record.',
  };
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

export interface LiveHistoryInputs {
  customerId: string;
  timeline: readonly TimelineItem[];
  /** W29 extension point input. Always [] until that wave lands. */
  orderScopeEntries?: readonly OrderScopeEntry[];
  /** W34 extension point input; fed from the profile since W35. */
  profileOrderProps?: ProfileOrderProps | null;
}

export function composeLiveHistoryEvents(inputs: LiveHistoryInputs): HistoryEvent[] {
  const events = projectTimelineItems(inputs.customerId, inputs.timeline);
  events.push(...projectOrderScopeEntries(inputs.customerId, inputs.orderScopeEntries ?? []));
  const lastOrder = projectProfileOrderProps(inputs.customerId, inputs.profileOrderProps ?? null);
  if (lastOrder) events.push(lastOrder);
  return events;
}

function monthYear(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('');
  return initials || '--';
}

/** Same mapping the Current Action screen uses for a real contact (W26). */
export function profileToCustomer(profile: ContactProfileRecord): MockCustomer {
  return {
    id: profile.id,
    initials: profile.initials,
    firstName: profile.firstName || profile.fullName.split(' ')[0] || profile.id,
    fullName: profile.fullName,
    customerSince: monthYear(profile.createdAt) ?? 'date unknown',
    city: profile.city || '',
    stateCode: profile.state || '',
    localTime: '',
    phone: profile.phone || '',
    email: profile.email || '',
    // W38: real HubSpot `preferred_channel` (set on 0 contacts today,
    // MEASURED 2026-10-20); '' renders as "not set" in the identity row.
    preferredChannel: typeof profile.preferredChannel === 'string' ? profile.preferredChannel.trim() : '',
  };
}

export function profileSinceLabel(profile: ContactProfileRecord): string {
  if (profile.lastCallDate) return `Last VanillaSoft call ${profile.lastCallDate}`;
  const created = monthYear(profile.createdAt);
  return created ? `In HubSpot since ${created}` : ' ';
}

export function profileOwner(profile: ContactProfileRecord): HistoryOwner {
  const name = profile.ownerName?.trim();
  if (name) return { initials: initialsOf(name), fullName: name };
  return { initials: '--', fullName: 'Unassigned' };
}

export function liveHistoryScope(session: HubSpotSession, customerId: string): HistoryScope {
  return {
    sessionId: `hubspot:${session.portalId}:${session.userId}`,
    generation: 1,
    workerId: session.userId,
    customerId,
  };
}

export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export interface BuildLiveHistorySourceArgs {
  customerId: string;
  session: HubSpotSession;
  profile: ContactProfileRecord;
  timeline: readonly TimelineItem[];
  capturedAt: number;
  timeZone?: string;
  orderScopeEntries?: readonly OrderScopeEntry[];
  profileOrderProps?: ProfileOrderProps | null;
}

export function buildLiveHistorySource(args: BuildLiveHistorySourceArgs): HistorySource {
  return {
    mode: 'live',
    scope: liveHistoryScope(args.session, args.customerId),
    customer: profileToCustomer(args.profile),
    timeZone: args.timeZone ?? localTimeZone(),
    capturedAt: args.capturedAt,
    events: composeLiveHistoryEvents({
      customerId: args.customerId,
      timeline: args.timeline,
      orderScopeEntries: args.orderScopeEntries ?? [],
      profileOrderProps: args.profileOrderProps ?? null,
    }),
    localNoteAuthor: args.session.email || args.session.userId,
    owner: profileOwner(args.profile),
    sinceLabel: profileSinceLabel(args.profile),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface TimelineReadState {
  status: 'loading' | 'ready' | 'error' | 'skipped';
  items: TimelineItem[];
  capturedAt: number | null;
  errorCode?: string;
}

/**
 * Reads /v1/contacts/:id/timeline?limit=50. Mirrors the W26 collection hook
 * but with the explicit limit this screen needs (that hook has no limit
 * argument and screens/_shared is outside W33's lane).
 */
export function useContactTimelineLimited(customerId: string | null | undefined, limit: number): TimelineReadState {
  const gate = useContactReadGate(customerId);
  const { canFetch, sessionIdentity, sessionToken } = gate;
  const [state, setState] = useState<TimelineReadState>(() => ({
    status: canFetch ? 'loading' : 'skipped',
    items: [],
    capturedAt: null,
  }));
  const latest = useRef<string | null>(null);

  useEffect(() => {
    if (!canFetch || !customerId || !gate.session) {
      setState({ status: 'skipped', items: [], capturedAt: null });
      return;
    }
    const requestId = `${customerId}:timeline:${limit}:${Date.now()}`;
    latest.current = requestId;
    setState({ status: 'loading', items: [], capturedAt: null });
    const headers = buildSessionHeaders(gate.session);
    const url = `/v1/contacts/${encodeURIComponent(customerId)}/timeline?limit=${encodeURIComponent(String(limit))}`;
    fetch(url, { method: 'GET', headers })
      .then(async (response) => {
        if (latest.current !== requestId) return;
        if (!response.ok) {
          let errorCode = `HTTP_${response.status}`;
          try {
            const parsed = JSON.parse(await response.text()) as { errorCode?: string };
            if (parsed && typeof parsed.errorCode === 'string') errorCode = parsed.errorCode;
          } catch {
            /* non-JSON or empty error body */
          }
          setState({ status: 'error', items: [], capturedAt: null, errorCode });
          return;
        }
        let body: unknown;
        try {
          body = await response.json();
        } catch {
          setState({ status: 'error', items: [], capturedAt: null, errorCode: 'BAD_JSON' });
          return;
        }
        const list = body && typeof body === 'object' ? (body as Record<string, unknown>)['items'] : null;
        setState({
          status: 'ready',
          items: Array.isArray(list) ? (list as TimelineItem[]) : [],
          capturedAt: Date.now(),
        });
      })
      .catch(() => {
        if (latest.current !== requestId) return;
        setState({ status: 'error', items: [], capturedAt: null, errorCode: 'FETCH_FAILED' });
      });
    return () => {
      if (latest.current === requestId) latest.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, customerId, limit, sessionIdentity, sessionToken]);

  return state;
}

export type LiveHistoryState =
  | { status: 'skipped' }
  | { status: 'loading' }
  | { status: 'error'; errorCode: string; detail: string }
  | { status: 'ready'; source: HistorySource };

export function useLiveHistorySource(customerId: string | null | undefined): LiveHistoryState {
  const gate = useContactReadGate(customerId);
  const profile = useContactProfile(customerId);
  const timeline = useContactTimelineLimited(customerId, LIVE_TIMELINE_LIMIT);
  const session = gate.session;

  return useMemo<LiveHistoryState>(() => {
    if (!gate.canFetch || !customerId || !session) return { status: 'skipped' };
    if (profile.status === 'skipped' || timeline.status === 'skipped') return { status: 'skipped' };
    if (profile.status === 'error') {
      return { status: 'error', errorCode: profile.errorCode ?? 'CONTACT_FETCH_FAILED', detail: 'The HubSpot contact read failed.' };
    }
    if (profile.status === 'empty') {
      return { status: 'error', errorCode: 'CONTACT_NOT_FOUND', detail: 'No HubSpot contact exists for this id.' };
    }
    if (timeline.status === 'error') {
      return { status: 'error', errorCode: timeline.errorCode ?? 'TIMELINE_FETCH_FAILED', detail: 'The HubSpot notes and calls read failed.' };
    }
    if (profile.status === 'loading' || timeline.status === 'loading' || !profile.contact || timeline.capturedAt === null) {
      return { status: 'loading' };
    }
    return {
      status: 'ready',
      source: buildLiveHistorySource({
        customerId,
        session,
        profile: profile.contact,
        timeline: timeline.items,
        capturedAt: timeline.capturedAt,
        // W29 extension point: nothing feeds this yet (route is 503).
        orderScopeEntries: [],
        // W34 extension point, fed by W35 from the profile already read.
        profileOrderProps: readProfileOrderProps(profile.contact),
      }),
    };
    // session identity is covered by gate.canFetch + the fetch hooks' own deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate.canFetch, customerId, session?.portalId, session?.userId, session?.email, profile.status, profile.contact, profile.errorCode, timeline.status, timeline.items, timeline.capturedAt, timeline.errorCode]);
}
